import { useMemo } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { AutoSaveList, ConfirmDialog, useFormReadOnly } from '../../../../common';
import { createCrisisPlanFormSchema } from './validationSchema';
import { CrisisPlanFields, CrisisPlanNotNeededCheckbox } from './components';
import { EMPTY_CRISIS_PLAN_VALUES } from './constants';
import {
  areAllRelatedBehaviorsSelected,
  getCrisisPlanDeleteMessage,
  getCrisisPlanRelatedBehaviorOptions,
  getCrisisPlanRowLabel,
  getSelectableRelatedBehaviors,
  mapCrisisPlanRowToValues,
  toCrisisPlanFieldPayload,
} from './utils';
import {
  useCrisisPlanNotNeededConfirm,
  useFetchTreatmentPlanV3CrisisPage,
  useTreatmentPlanV3CrisisPlans,
} from './hooks';
import { TreatmentPlanV3PageContent } from '../components';

import type {
  CrisisPlanFormValues,
  CrisisPlanPayload,
  ITreatmentPlanV3CrisisPlan,
} from '../../../../common';

export default function CrisisPlanForm() {
  const { retry } = useFetchTreatmentPlanV3CrisisPage();
  const {
    crisisPlans,
    behaviors,
    noCrisisPlanNeeded,
    noMaladaptiveBehavior,
    crisisPageReady,
    hasCrisisLoadError,
    assessmentPageReady,
    hasAssessmentLoadError,
    isLoading,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoCrisisPlanNeeded,
  } = useTreatmentPlanV3CrisisPlans();
  const readOnly = useFormReadOnly();
  const { isConfirmOpen, handleToggle, handleConfirm, closeConfirm } =
    useCrisisPlanNotNeededConfirm({
      hasSavedCrisisPlans: crisisPlans.length > 0,
      clearAll,
      setNoCrisisPlanNeeded,
    });
  const hasSelectableBehaviors = getSelectableRelatedBehaviors(behaviors).length > 0;
  const validationSchema = useMemo(
    () => createCrisisPlanFormSchema(hasSelectableBehaviors),
    [hasSelectableBehaviors],
  );

  let pageBody;
  if (hasCrisisLoadError || hasAssessmentLoadError) {
    pageBody = (
      <Stack gap={1} alignItems="flex-start">
        <Typography>
          {hasCrisisLoadError
            ? 'Unable to load crisis plans. Crisis plans cannot be changed until this succeeds.'
            : 'Unable to load assessment information. Crisis plans cannot be changed until this succeeds.'}
        </Typography>
        <Button onClick={retry} variant="contained">
          Retry
        </Button>
      </Stack>
    );
  } else if (!crisisPageReady || !assessmentPageReady) {
    pageBody = (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  } else {
    let crisisPlanList;
    if (noCrisisPlanNeeded) {
      crisisPlanList = null;
    } else if (noMaladaptiveBehavior) {
      crisisPlanList = (
        <Typography>
          Uncheck "No Maladaptive behavior" on the Assessment page to add a crisis plan.
        </Typography>
      );
    } else {
      crisisPlanList = (
        <Stack gap={2}>
          {hasSelectableBehaviors ? null : (
            <Typography>
              Add a maladaptive behavior on the Assessment page to select it as a related behavior.
              You can save the other fields now.
            </Typography>
          )}
          <AutoSaveList<CrisisPlanFormValues, ITreatmentPlanV3CrisisPlan, CrisisPlanPayload>
            addButtonLabel="Add crisis plan"
            addDisabled={areAllRelatedBehaviorsSelected(behaviors, crisisPlans)}
            addDisabledMessage="All related behaviors are taken."
            disabled={isLoading || isBulkSaving}
            emptyValues={EMPTY_CRISIS_PLAN_VALUES}
            entries={crisisPlans}
            getDeleteConfirm={(row) => ({
              title: 'Confirm crisis plan deletion?',
              message: getCrisisPlanDeleteMessage(row, behaviors),
            })}
            getRowKey={(row) => row.id}
            getRowLabel={(row) => getCrisisPlanRowLabel(row, behaviors)}
            isSaving={isSaving}
            mapRowToValues={mapCrisisPlanRowToValues}
            newEntryLabel="New crisis plan"
            onCreate={create}
            onDelete={remove}
            onUpdate={update}
            plainDraft
            requireCompleteDraft={false}
            renderFields={({
              formik,
              disabled,
              readOnly: fieldsReadOnly,
              saveField,
              backgroundColor,
              row,
            }) => (
              <CrisisPlanFields
                backgroundColor={backgroundColor}
                disabled={disabled}
                formik={formik}
                readOnly={fieldsReadOnly}
                relatedBehaviorOptions={getCrisisPlanRelatedBehaviorOptions(
                  behaviors,
                  crisisPlans,
                  row?.id,
                )}
                relatedBehaviorRequired={hasSelectableBehaviors}
                saveField={saveField}
              />
            )}
            toFieldPayload={toCrisisPlanFieldPayload}
            validationSchema={validationSchema}
          />
        </Stack>
      );
    }

    pageBody = (
      <>
        <CrisisPlanNotNeededCheckbox
          checked={noCrisisPlanNeeded}
          disabled={isLoading || readOnly || isSaving}
          onChange={handleToggle}
        />

        {crisisPlanList}

        <ConfirmDialog
          open={isConfirmOpen}
          title="Remove all crisis plans?"
          message={
            <>
              Marking "Crisis plan for this client not needed" will permanently delete every crisis
              plan you've entered.
              <br />
              This action can't be undone.
            </>
          }
          confirmLabel="Remove all"
          confirmLoading={isBulkSaving}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />
      </>
    );
  }

  return <TreatmentPlanV3PageContent title="Crisis plan">{pageBody}</TreatmentPlanV3PageContent>;
}
